import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ConversationType, Message, MessageType, ReviewInvitationLinkResponse, TripMatch, TripMatchStatus, TripRequest, TripRequestStatus,
} from '@marageti/z4-sdk/lib/travel';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import {
  useApiClient,
  useFetch,
  TemplatePluginHandle,
  OutlinedIcons,
  ContextMenuItemType,
  ComposerRestoreContent,
} from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { useConfirm } from 'material-ui-confirm';
import { getConversationTypeTitle } from '../../../components/conversation-links';
import ConversationView from './conversation-view';
import useZicassoChat from '../../../hooks/use-zicasso-chat';
import { useAuthStore } from '../../../store';

const ConversationContainer = () => {
  const {
    conversationId, tripMatchId, tripRequestId,
  } = useParams();
  const [searchParams] = useSearchParams();
  const viewerId = searchParams.get('viewerId');
  const apiClient = useApiClient();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loggedInTeamMember } = useAuthStore();
  const [logCallModalOpen, setLogCallModalOpen] = useState(false);
  const [logInternalNoteModalOpen, setLogInternalNoteModalOpen] = useState(false);
  const [sendSMSModalOpen, setSendSMSModalOpen] = useState(false);
  const [isCopyingTrustpilotLink, setIsCopyingTrustpilotLink] = useState(false);
  const tripMatch = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], !!tripMatchId);
  const tripRequest = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [tripRequestId], !!tripRequestId);
  const [showChooseTemplate, setShowChooseTemplate] = useState(false);
  const [restoreEditorContent, setRestoreEditorContent] = useState<
  ComposerRestoreContent | undefined
  >(undefined);
  const templatePluginRef = useRef<TemplatePluginHandle>(null);

  useEffect(() => {
    setRestoreEditorContent(undefined);
  }, [conversationId]);

  const viewer = {
    id: viewerId || loggedInTeamMember?.id,
    type: PersonType.TeamMember,
  };

  const isAdmin = loggedInTeamMember?.userInfo?.some((info) => info.roles?.includes(RoleName.ADMIN)) || false;

  const {
    participants,
    conversation,
    messages,
    sendMessage,
    loadMoreMessages,
    hasMoreMessages,
    lastSeenMessageId,
    isError,
    isLoading,
    isValidating,
    deleteMessage,
    updateMessages,
  } = useZicassoChat({
    conversationId,
    viewerId: viewer?.id,
    tripMatchId,
    tripRequestId,
  });

  const conversationType = conversation?.conversationType;

  const setTemplateText = (value: string) => {
    if (templatePluginRef.current) {
      templatePluginRef.current.insertTemplate(value);
    }
  };

  const getConversationTitle = () => {
    if (conversationType) {
      return getConversationTypeTitle({
        conversationType,
      });
    }
    return '';
  };

  const handleOnSend = (messageText: string) => {
    sendMessage({
      conversationId,
      text: messageText,
      messageType: MessageType.Text,
    });
  };

  const handleUndoSend = async (message: Message) => {
    const html = message.text ?? '';
    try {
      await deleteMessage(message.id);
      setRestoreEditorContent({ key: Date.now(), html });
    } catch {
      enqueueSnackbar('Failed to delete message.', { variant: 'default' });
    }
  };

  const tripRequestIsUnassigned = !tripRequest.isLoading && !!tripRequestId && !tripRequest.data?.assignedTo;
  const tripRequestIsSold = !tripRequest.isLoading && tripRequest.data?.status === TripRequestStatus.SoldStatus;

  const tripMatchStatus = tripMatch.data?.status;

  const determineIsReadOnly = useMemo(() => {
    if (tripRequestIsUnassigned) {
      return true;
    }
    switch (conversationType) {
      case ConversationType.TravelerToZicasso: // Care Team can send messages
        return false;
      case ConversationType.TravelerToPartner: // Read-only
        return true;
      case ConversationType.ZicassoToPartner: // Read-only if the trip match is declined or has a match error
        return tripMatchStatus === TripMatchStatus.MatchPartnerDeclined
          || tripMatchStatus === TripMatchStatus.MatchZicassoMatchError;
      default:
        return true;
    }
  }, [conversationType, tripRequestIsUnassigned, tripMatchStatus]);

  const handleDocumentClick = async (message: Message) => {
    if (!message.entityId) {
      return;
    }

    try {
      const doc = await apiClient.documentsClient.getDocument(message.entityId);
      if (doc.sourceURI) {
        window.open(doc.sourceURI, '_blank');
        return;
      }

      const downloadURL = await apiClient.documentsClient.getDocumentDownloadUrl(message.entityId);
      window.open(downloadURL.url, '_blank');
    } catch (error: any) {
      const snackbarMessage = error?.code === 404 ? 'Document not found' : 'Error downloading document';
      enqueueSnackbar(snackbarMessage, { variant: 'default' });
    }
  };

  const handleSalesRecordingClick = async (message: Message) => {
    const salesRecordingId = message.entityId;
    if (salesRecordingId) {
      navigate(`/trip-matches/${tripMatchId}/sales/${message.entityId}`);
    }
  };

  const handleNonEntityViewClick = (message: Message) => {
    if (message.messageType === MessageType.TripMatchCanceled) {
      navigate(`/trip-matches/${tripMatchId}`);
    }
    if (message.messageType === MessageType.TripRequestCanceled) {
      navigate(`/trip-requests/${tripRequestId}`);
    }
  };

  const handleViewClick = (message: Message) => {
    switch (message.entityType) {
      case 'Document':
        handleDocumentClick(message);
        break;
      case 'SalesRecording':
        handleSalesRecordingClick(message);
        break;
      default:
        handleNonEntityViewClick(message);
        break;
    }
  };

  const handleLogCallOpen = () => {
    setLogCallModalOpen(true);
  };

  const handleLogCallClose = () => {
    setLogCallModalOpen(false);
  };

  const handleLogInternalNoteOpen = () => {
    setLogInternalNoteModalOpen(true);
  };

  const handleLogInternalNoteClose = () => {
    setLogInternalNoteModalOpen(false);
  };

  const handleSendSMSOpen = () => {
    setSendSMSModalOpen(true);
  };

  const handleSendSMSClose = () => {
    setSendSMSModalOpen(false);
  };

  const handleTrustpilotLinkCopy = (closeMenu?: () => void) => {
    if (isCopyingTrustpilotLink) return;

    setIsCopyingTrustpilotLink(true);
    apiClient.tripRequestsClient.postReviewInvitationLink(tripRequestId!).then(
      (res: ReviewInvitationLinkResponse) => {
        navigator.clipboard.writeText(res.link);
        enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
      },
      () => {
        enqueueSnackbar('Failed! Please try again.', { variant: 'default' });
      },
    ).finally(() => {
      setIsCopyingTrustpilotLink(false);
      closeMenu?.();
    });
  };

  const handleDeleteClick = (message: Message) => {
    switch (message.messageType) {
      case MessageType.PhoneCall:
      case MessageType.PhoneCallUnanswered:
        confirm({
          title: 'Delete Phone Call Log?',
          description: 'This log will be permanently deleted from the message thread.',
          confirmationText: 'Delete',
        }).then(async () => {
          try {
            await deleteMessage(message.id);
            enqueueSnackbar('Phone call log deleted.', { variant: 'success' });
          } catch (error) {
            enqueueSnackbar('Failed to delete phone call log.', { variant: 'default' });
          }
        }).catch(() => {});
        break;

      case MessageType.ZicassoNote:
        confirm({
          title: 'Delete Internal Note?',
          description: 'This note will be permanently deleted from the message thread.',
          confirmationText: 'Delete',
        }).then(async () => {
          try {
            await deleteMessage(message.id);
            enqueueSnackbar('Internal note deleted.', { variant: 'success' });
          } catch (error) {
            enqueueSnackbar('Failed to delete internal note.', { variant: 'default' });
          }
        }).catch(() => {});
        break;
      case MessageType.SMS:
      case MessageType.SMSText:
        confirm({
          title: 'Delete Log?',
          description: 'This log will be permanently deleted from the message thread.',
          confirmationText: 'Delete',
        }).then(async () => {
          try {
            await deleteMessage(message.id);
            enqueueSnackbar('Log deleted.', { variant: 'success' });
          } catch (error) {
            enqueueSnackbar('Failed to delete log.', { variant: 'default' });
          }
        }).catch(() => {});
        break;
      default:
        break;
    }
  };

  const primaryTraveler = tripMatch.data?.travelers?.[0] || tripRequest.data?.travelers?.[0];
  const primaryTravelerId = primaryTraveler?.id;

  const contextMenuItems = useMemo(() => {
    const primaryTravelerPhoneNumberProvided = !!primaryTraveler?.phone?.number;

    const baseItems: ContextMenuItemType[] = [
      {
        icon: <OutlinedIcons.OutlinedPencilAltIcon color="primary" />,
        action: handleLogInternalNoteOpen,
        label: 'Log Internal Note',
      },
      {
        icon: <OutlinedIcons.OutlinedPhoneOutgoingIcon color="primary" />,
        action: handleLogCallOpen,
        label: 'Log Call / Text Message',
      },
      ...(conversationType === ConversationType.TravelerToZicasso ? [{
        icon: <OutlinedIcons.OutlinedDeviceMobileIcon color="primary" />,
        action: handleSendSMSOpen,
        label: 'Send SMS',
        disabled: !primaryTravelerPhoneNumberProvided,
      } as ContextMenuItemType] : []),
    ];

    if (tripRequestIsSold && conversationType === ConversationType.TravelerToZicasso) {
      baseItems.push({
        icon: <OutlinedIcons.OutlinedLinkIcon color="primary" />,
        autoClose: false,
        action: handleTrustpilotLinkCopy,
        label: isCopyingTrustpilotLink ? 'Creating URL...' : 'Copy Trustpilot Review Link',
      });
    }

    return baseItems;
  }, [tripRequestIsSold, conversationType, isCopyingTrustpilotLink, tripMatch, tripRequest, primaryTraveler]);

  return (
    <ConversationView
      conversation={conversation}
      conversationTitle={getConversationTitle()}
      handleViewClick={handleViewClick}
      handleDeleteClick={isAdmin ? handleDeleteClick : undefined}
      isError={isError}
      isLoading={isLoading}
      isReadOnly={determineIsReadOnly}
      isValidating={isValidating}
      loadMoreMessages={loadMoreMessages}
      hasMoreMessages={hasMoreMessages}
      messages={messages}
      lastMessageSeenId={lastSeenMessageId}
      participants={participants}
      onSend={handleOnSend}
      setShowChooseTemplate={setShowChooseTemplate}
      setTemplateText={setTemplateText}
      showAssignedToToolbar={tripRequestIsUnassigned}
      showChooseTemplate={showChooseTemplate}
      templatePluginRef={templatePluginRef}
      tripMatchId={tripMatchId}
      tripRequestId={tripRequestId}
      viewer={viewer}
      logCallModalOpen={logCallModalOpen}
      handleLogCallClose={handleLogCallClose}
      logInternalNoteModalOpen={logInternalNoteModalOpen}
      handleLogInternalNoteClose={handleLogInternalNoteClose}
      sendSMSModalOpen={sendSMSModalOpen}
      handleSendSMSClose={handleSendSMSClose}
      travelerId={primaryTravelerId}
      contextMenuItems={contextMenuItems}
      onUndoSend={handleUndoSend}
      restoreEditorContent={restoreEditorContent}
      onAfterLogCallMessageSent={updateMessages}
    />
  );
};

export default ConversationContainer;
